import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageForm from "components/pageForm";
import Button from "components/button";
import LoadingButton from "components/button/loading";
import UseContext from "hooks/useContext";
import useFetchData from "hooks/useFetchData";
import ApiDataBase from "server";
import TemplateSubNav from "pages/missionTemplates/components/TemplateSubNav";
import TemplateIndicator from "pages/missionTemplates/components/TemplateIndicator";
import TemplateFormsCreate from "pages/missionTemplates/templateFormsCreate";
import {
    isTemplateIntituleFilled,
    isTemplateNameTaken,
} from "pages/missionTemplates/utils/templateIntitule";
import { normalizeTemplateTools } from "pages/missionTemplates/utils/templateTools";
import {
    buildTemplateConditions,
    hasSelectedPenibilite,
    normalizePenibilite,
} from "pages/missionTemplates/utils/templateRiskyPositions";
import { normalizeDressCode } from "pages/missionTemplates/utils/templateDressCode";
import {
    isBelowMinimumWage,
    useMinimumWage,
} from "pages/missionTemplates/utils/templateMinimumWage";
import {
    buildTaskRows,
    buildTasksPayload,
    countSelectedTasks,
    findQualification,
    getQualificationTaskProposals,
} from "pages/missionTemplates/utils/templateTasks";
import { getQualificationTypeOfActivity } from "pages/missionTemplates/body/activity.template";
import {
    TEMPLATE_STEP,
    TEMPLATE_STEPS_COUNT,
    getTemplateStepKey,
    getTemplateStepPage,
} from "pages/missionTemplates/templateSteps";

const CreateOfTemplate = ({ mode = "create" }) => {
    useFetchData();
    const navigate = useNavigate();
    const { id } = useParams();
    const {
        templatePage,
        templateData,
        setTemplateData,
        setTemplatePage,
        handleTemplatePrev,
        handleTemplateNext,
        companyData,
        resetTemplateData,
        refreshMissionTemplates,
        setRefreshMissionTemplates,
        missionTemplates,
        setMissionTemplates,
        missionTemplatesCompanyId,
        setMissionTemplatesCompanyId,
        language,
    } = UseContext();
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [isLoadingTemplate, setIsLoadingTemplate] = useState(mode === "edit");
    const hasCompanyLoaded = companyData?._id != null;
    const minimumWage = useMinimumWage();
    const t = (key, fallback) =>
        language.getValue(`missionTemplate.wizard.${key}`, fallback);

    useEffect(() => {
        if (mode === "create") {
            resetTemplateData();
        }
        // eslint-disable-next-line
    }, [mode]);

    // The wizard is reachable by url: without this the name uniqueness check
    // would run against the templates of no company, or of the previous one.
    useEffect(() => {
        if (!companyData?._id) return undefined;
        const requestedId = String(companyData._id);
        if (missionTemplatesCompanyId === requestedId) return undefined;
        let cancelled = false;
        ApiDataBase.getMissionTemplates(
            { idCompany: requestedId },
            (res) => {
                if (cancelled) return;
                setMissionTemplates(res?.templates || []);
                setMissionTemplatesCompanyId(requestedId);
            },
            (err) => {
                if (cancelled) return;
                console.error(err);
            }
        );
        return () => {
            cancelled = true;
        };
    }, [
        companyData?._id,
        missionTemplatesCompanyId,
        setMissionTemplates,
        setMissionTemplatesCompanyId,
    ]);

    useEffect(() => {
        if (mode !== "edit" || !id) return;
        let cancelled = false;
        setIsLoadingTemplate(true);
        // The Tasks step needs the qualification's proposals to show the
        // unchecked ones: both requests run together, a failed list only
        // loses the proposals.
        let loadedTemplate = null;
        let loadedQualifications = null;
        const fillWizard = () => {
            if (cancelled || !loadedTemplate || !loadedQualifications) return;
            const template = loadedTemplate;
            const qualification = findQualification(
                loadedQualifications,
                template.qualification,
                getQualificationTypeOfActivity(template.activity)
            );
            setTemplateData({
                name: template.name || "",
                activity: template.activity || "",
                qualification: template.qualification || "",
                tools: normalizeTemplateTools(template.tools),
                licences: template.licences || [],
                languages: template.language || [],
                dressCode:
                    typeof template.dressCode === "string"
                        ? template.dressCode
                        : "",
                tasks: buildTaskRows({
                    proposals: getQualificationTaskProposals(qualification),
                    templateTasks: template.tasks,
                }),
                contact_name: template.contact?.name || "",
                contact_poste: template.contact?.poste || "",
                location: template.location || "",
                salary:
                    typeof template.salary?.wage_per_hour === "number"
                        ? String(template.salary.wage_per_hour)
                        : "",
                isIndexedToMinimumWage:
                    !!template.isIndexedToMinimumWage,
                position_at_risk: !!template.conditions?.position_at_risk,
                penibilite: normalizePenibilite(
                    template.conditions?.penibilite
                ),
            });
            setTemplatePage(1);
            setIsLoadingTemplate(false);
        };
        ApiDataBase.getMissionTemplate(
            { id },
            (template) => {
                loadedTemplate = template || {};
                fillWizard();
            },
            (err) => {
                if (cancelled) return;
                console.error(err);
                toast.error(t("notFound", "Fiche de poste introuvable"));
                navigate("/missionTemplates");
            }
        );
        ApiDataBase.getListQualifications(
            {},
            (res) => {
                loadedQualifications = Array.isArray(res) ? res : [];
                fillWizard();
            },
            (err) => {
                console.warn(err);
                loadedQualifications = [];
                fillWizard();
            }
        );
        return () => {
            cancelled = true;
        };
    }, [id, mode, navigate, setTemplateData, setTemplatePage]);

    const validateCurrentStep = () => {
        switch (getTemplateStepKey(templatePage)) {
            case TEMPLATE_STEP.ACTIVITY:
                if (!templateData.activity || !templateData.qualification) {
                    toast.error(
                        t(
                            "activityRequired",
                            "Veuillez sélectionner secteur et qualification"
                        )
                    );
                    return false;
                }
                if (!isTemplateIntituleFilled(templateData)) {
                    toast.error(
                        t(
                            "nameRequired",
                            "Veuillez indiquer l'intitulé de la fiche de poste"
                        )
                    );
                    return false;
                }
                if (isTemplateNameTaken(templateData.name, missionTemplates, id)) {
                    toast.error(
                        t("nameTaken", "Une fiche de poste porte déjà ce nom")
                    );
                    return false;
                }
                return true;
            case TEMPLATE_STEP.RISKY_POSITION:
                if (
                    templateData.position_at_risk &&
                    !hasSelectedPenibilite(templateData.penibilite)
                ) {
                    toast.error(
                        language.getValue(
                            "missionTemplate.riskyPosition.selectionRequired",
                            "Veuillez sélectionner au moins un poste à risque"
                        )
                    );
                    return false;
                }
                return true;
            case TEMPLATE_STEP.TASKS:
                if (countSelectedTasks(templateData.tasks) === 0) {
                    toast.error(
                        t(
                            "tasksRequired",
                            "Veuillez retenir au moins une tâche"
                        )
                    );
                    return false;
                }
                return true;
            case TEMPLATE_STEP.LOCATION_SALARY:
                if (
                    !templateData.location ||
                    (typeof templateData.location === "object" &&
                        Object.keys(templateData.location).length === 0)
                ) {
                    toast.error(
                        t("locationRequired", "Veuillez renseigner une adresse")
                    );
                    return false;
                }
                if (!templateData.salary) {
                    toast.error(
                        t("salaryRequired", "Veuillez renseigner un taux horaire")
                    );
                    return false;
                }
                if (isBelowMinimumWage(templateData, minimumWage)) {
                    return false;
                }
                if (
                    !templateData.contact_name?.trim() ||
                    !templateData.contact_poste?.trim()
                ) {
                    toast.error(
                        t(
                            "contactRequired",
                            "Veuillez compléter les informations du responsable sur place"
                        )
                    );
                    return false;
                }
                return true;
            default:
                return true;
        }
    };

    // These rules disable "Next" instead of raising a toast: the reason
    // is already shown on the step (task counter, inline minimum wage message).
    const isNextBlocked = (() => {
        switch (getTemplateStepKey(templatePage)) {
            case TEMPLATE_STEP.TASKS:
                return countSelectedTasks(templateData.tasks) === 0;
            case TEMPLATE_STEP.LOCATION_SALARY:
                return isBelowMinimumWage(templateData, minimumWage);
            default:
                return false;
        }
    })();

    const handleNextClick = () => {
        if (validateCurrentStep()) handleTemplateNext();
    };

    const buildPayload = () => ({
        name: templateData.name?.trim(),
        title: templateData.qualification,
        activity: templateData.activity,
        qualification: templateData.qualification,
        tools: templateData.tools,
        licences: templateData.licences,
        language: templateData.languages,
        dressCode: normalizeDressCode(templateData.dressCode),
        tasks: buildTasksPayload(templateData.tasks),
        contact: {
            name: templateData.contact_name,
            poste: templateData.contact_poste,
        },
        location: templateData.location,
        salary: { wage_per_hour: templateData.salary },
        isIndexedToMinimumWage: !!templateData.isIndexedToMinimumWage,
        conditions: buildTemplateConditions(templateData),
    });

    const handleSubmitError = (err, fallbackMessage) => {
        console.error(err);
        setIsSubmitLoading(false);
        if (err?.response?.data?.message === "Template name already used") {
            toast.error(t("nameTaken", "Une fiche de poste porte déjà ce nom"));
            setTemplatePage(getTemplateStepPage(TEMPLATE_STEP.ACTIVITY));
            return;
        }
        if (
            err?.response?.status === 400 &&
            err.response.data?.message === "Risky positions required"
        ) {
            toast.error(
                language.getValue(
                    "missionTemplate.riskyPosition.selectionRequired",
                    "Veuillez sélectionner au moins un poste à risque"
                )
            );
            setTemplatePage(getTemplateStepPage(TEMPLATE_STEP.RISKY_POSITION));
            return;
        }
        toast.error(fallbackMessage);
    };

    const handleSubmit = () => {
        setIsSubmitLoading(true);
        const payload = buildPayload();
        if (mode === "edit") {
            ApiDataBase.patchMissionTemplate(
                { id, data: payload },
                () => {
                    setIsSubmitLoading(false);
                    setRefreshMissionTemplates(!refreshMissionTemplates);
                    setTemplatePage(1);
                    resetTemplateData();
                    toast.success(t("updated", "Fiche de poste mise à jour"));
                    navigate("/missionTemplates");
                },
                (err) =>
                    handleSubmitError(
                        err,
                        t("updateFailed", "Modification impossible")
                    )
            );
        } else {
            ApiDataBase.postCreateMissionTemplate(
                {
                    data: payload,
                    idCompany: companyData?._id,
                },
                () => {
                    setIsSubmitLoading(false);
                    setRefreshMissionTemplates(!refreshMissionTemplates);
                    setTemplatePage(1);
                    resetTemplateData();
                    toast.success(t("created", "Fiche de poste créée"));
                    navigate("/missionTemplates");
                },
                (err) =>
                    handleSubmitError(
                        err,
                        t("createFailed", "Création impossible")
                    )
            );
        }
    };

    const isLastStep = templatePage === TEMPLATE_STEPS_COUNT;

    if (!hasCompanyLoaded) {
        return (
            <PageForm>
                <div className="col-span-full">
                    <div className="w-full mx-auto lg:w-[800px] px-6 py-8">
                        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
                    </div>
                </div>
            </PageForm>
        );
    }

    return (
        <PageForm>
            <div className="col-span-12 bg-white sticky z-10 top-16">
                <TemplateSubNav />
            </div>
            <div className="col-span-full pb-24">
                <div className="w-full mx-auto lg:w-[800px] px-6 py-8">
                    {isLoadingTemplate ? (
                        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
                    ) : (
                        <TemplateFormsCreate />
                    )}
                </div>
            </div>
            {!isLoadingTemplate && (
                <div className="col-span-full">
                    <div className="fixed bottom-0 left-[80px] sidebar-expanded:left-64 2xl:left-64 right-0 bg-white border-t border-gray-100 z-20">
                        <div className="w-full mx-auto lg:w-[800px] px-6 py-4">
                            <div className="flex justify-between items-center gap-4">
                                <TemplateIndicator formIndex={templatePage} />
                                <div className="flex gap-3 shrink-0">
                                    {templatePage > 1 && (
                                        <Button
                                            size={"LG"}
                                            color={"SECONDARY"}
                                            onClick={handleTemplatePrev}
                                        >
                                            {t("back", "Retour")}
                                        </Button>
                                    )}
                                    {isLastStep ? (
                                        isSubmitLoading ? (
                                            <LoadingButton size={"LG"}>
                                                {t("saving", "Enregistrement…")}
                                            </LoadingButton>
                                        ) : (
                                            <Button
                                                size={"LG"}
                                                onClick={handleSubmit}
                                            >
                                                {t("save", "Enregistrer la fiche")}
                                            </Button>
                                        )
                                    ) : (
                                        <Button
                                            size={"LG"}
                                            color={
                                                isNextBlocked
                                                    ? "DISABLED"
                                                    : "PRIMARY"
                                            }
                                            className={
                                                isNextBlocked
                                                    ? "cursor-not-allowed"
                                                    : ""
                                            }
                                            onClick={handleNextClick}
                                            disabled={isNextBlocked}
                                        >
                                            {t("next", "Suivant")}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </PageForm>
    );
};

export default CreateOfTemplate;
